export async function fileToCanvas(file) {
  if (!(file instanceof File)) {
    throw new Error('Se esperaba un archivo para OCR.');
  }
  if (file.type === 'application/pdf') {
    const pages = await renderPdfToCanvases(file);
    return pages[0];
  }
  const image = await loadImageFromFile(file);
  return imageToCanvas(image);
}

export async function fileToCanvases(file) {
  if (!(file instanceof File)) {
    throw new Error('Se esperaba un archivo para OCR.');
  }
  if (file.type === 'application/pdf') {
    return renderPdfToCanvases(file);
  }
  return [imageToCanvas(await loadImageFromFile(file))];
}

export async function extractPdfText(file) {
  if (!(file instanceof File) || file.type !== 'application/pdf') {
    return '';
  }
  const pdfjs = await loadPdfJs();
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages = await Promise.all(
    Array.from({ length: pdf.numPages }, async (_, index) => {
      const page = await pdf.getPage(index + 1);
      const content = await page.getTextContent();
      return content.items.map(item => item.str || '').join(' ');
    }),
  );
  return pages.join('\n');
}

async function renderPdfToCanvases(file) {
  const pdfjs = await loadPdfJs();
  const data = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pages = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    pages.push(canvas);
  }
  return pages;
}

async function loadPdfJs() {
  if (window.__pdfjsPromise) {
    return window.__pdfjsPromise;
  }
  window.__pdfjsPromise = import(/* @vite-ignore */ 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/legacy/build/pdf.mjs').then(
    module => {
      const pdfjs = module;
      pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/legacy/build/pdf.worker.mjs';
      return pdfjs;
    },
  );
  return window.__pdfjsPromise;
}

async function loadImageFromFile(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

export function imageToCanvas(image) {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0);
  return canvas;
}

export function preprocessCanvas(canvas) {
  const context = canvas.getContext('2d');
  const { width, height } = canvas;
  const imageData = context.getImageData(0, 0, width, height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const normalized = gray > 168 ? 255 : gray < 60 ? 0 : gray;
    data[i] = normalized;
    data[i + 1] = normalized;
    data[i + 2] = normalized;
  }
  context.putImageData(imageData, 0, 0);
  return canvas;
}

export async function canvasToBlob(canvas) {
  return new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), 'image/png', 1);
  });
}
